import React from 'react';

/**
 * Performance Metrics Display - Summary cards and color breakdown
 */
export default function PerformanceMetricsDisplay({ metrics }) {
  if (!metrics) return null;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Total Items Completed"
          value={metrics.total_items_completed}
          color="gray"
        />
        <MetricCard
          label="Avg Completion Time"
          value={formatTime(metrics.average_completion_seconds)}
          color="blue"
        />
        <MetricCard
          label="Fastest"
          value={metrics.fastest_completion_seconds ? formatTime(metrics.fastest_completion_seconds) : '-'}
          color="green"
        />
        <MetricCard
          label="Slowest"
          value={metrics.slowest_completion_seconds ? formatTime(metrics.slowest_completion_seconds) : '-'}
          color="red"
        />
      </div>

      {/* Color Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Completion Time Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <ColorCard color="green" count={metrics.by_color.green} label="Under 5 min" />
          <ColorCard color="yellow" count={metrics.by_color.yellow} label="5-10 min" />
          <ColorCard color="orange" count={metrics.by_color.orange} label="10-15 min" />
          <ColorCard color="red" count={metrics.by_color.red} label="Over 15 min" />
        </div>
        
        {/* Visual Bar */}
        <div className="mt-4 h-6 rounded-full overflow-hidden flex bg-gray-200 dark:bg-gray-700">
          {metrics.total_items_completed > 0 && (
            <>
              <div 
                className="bg-green-500 h-full transition-all" 
                style={{ width: `${(metrics.by_color.green / metrics.total_items_completed) * 100}%` }}
              />
              <div 
                className="bg-yellow-500 h-full transition-all" 
                style={{ width: `${(metrics.by_color.yellow / metrics.total_items_completed) * 100}%` }}
              />
              <div 
                className="bg-orange-500 h-full transition-all" 
                style={{ width: `${(metrics.by_color.orange / metrics.total_items_completed) * 100}%` }}
              />
              <div 
                className="bg-red-500 h-full transition-all" 
                style={{ width: `${(metrics.by_color.red / metrics.total_items_completed) * 100}%` }}
              />
            </>
          )}
        </div>
        
        {/* Percentages */}
        {metrics.total_items_completed > 0 && (
          <div className="mt-2 flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>{Math.round((metrics.by_color.green / metrics.total_items_completed) * 100)}% green</span>
            <span>{Math.round((metrics.by_color.yellow / metrics.total_items_completed) * 100)}% yellow</span>
            <span>{Math.round((metrics.by_color.orange / metrics.total_items_completed) * 100)}% orange</span>
            <span>{Math.round((metrics.by_color.red / metrics.total_items_completed) * 100)}% red</span>
          </div>
        )}
      </div>

      {/* By Station */}
      {Object.keys(metrics.by_station || {}).length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Performance by Station</h3>
          <div className="space-y-3">
            {Object.entries(metrics.by_station).map(([station, data]) => (
              <div key={station} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white capitalize">{station}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {data.total} items - Avg: {formatTime(data.avg_seconds)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-sm font-medium">{data.green}</span>
                  <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded text-sm font-medium">{data.yellow}</span>
                  <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded text-sm font-medium">{data.orange}</span>
                  <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded text-sm font-medium">{data.red}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper: Format seconds to MM:SS
function formatTime(seconds) {
  if (!seconds && seconds !== 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

// Metric Card Component
function MetricCard({ label, value, color }) {
  const colorClasses = {
    gray: 'text-gray-900 dark:text-white',
    blue: 'text-blue-600 dark:text-blue-400',
    green: 'text-green-600 dark:text-green-400',
    red: 'text-red-600 dark:text-red-400'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`text-3xl font-bold ${colorClasses[color]}`}>{value}</p>
    </div>
  );
}

// Color Card Component
function ColorCard({ color, count, label }) {
  const bgClasses = {
    green: 'bg-green-50 dark:bg-green-900/20',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20',
    orange: 'bg-orange-50 dark:bg-orange-900/20',
    red: 'bg-red-50 dark:bg-red-900/20'
  };
  
  const dotClasses = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500'
  };
  
  const textClasses = {
    green: 'text-green-700 dark:text-green-400',
    yellow: 'text-yellow-700 dark:text-yellow-400',
    orange: 'text-orange-700 dark:text-orange-400',
    red: 'text-red-700 dark:text-red-400'
  };
  
  const labelClasses = {
    green: 'text-green-600 dark:text-green-500',
    yellow: 'text-yellow-600 dark:text-yellow-500',
    orange: 'text-orange-600 dark:text-orange-500',
    red: 'text-red-600 dark:text-red-500'
  };

  return (
    <div className={`text-center p-4 ${bgClasses[color]} rounded-xl`}>
      <div className={`w-8 h-8 ${dotClasses[color]} rounded-full mx-auto mb-2`} />
      <p className={`text-2xl font-bold ${textClasses[color]}`}>{count}</p>
      <p className={`text-sm ${labelClasses[color]}`}>{label}</p>
    </div>
  );
}
